// Combined /dashboard — the client-facing homepage that folds the per-client
// analytics contract (leads/traffic/local/reviews/rankings/ads) into the SEO
// Overview's visual grammar (SectionPanel, MetricCard-style KPIs, period
// selector). Design source of truth: Claude Design project
// c733b4d7-8893-43ba-8c64-1ce003b78b83, "Combined Dashboard v2.dc.html".
// Ruling (Carl/Chans, 2026-08-06): analytics reporting only — no triage strip,
// regressions surface only as pill deltas.
//
// Honors the same 4-state grammar as the retired /analytics page (loading /
// error / not-connected / empty) and adaptive section hiding: a section with no data
// simply doesn't render (Advertising is the one exception — it always shows,
// as either the KPI strip or the not-connected teaser).

import { useClientAnalytics } from "@/hooks/useClientAnalytics";
import { useClientAnalyticsEmbed } from "@/hooks/useClientAnalyticsEmbed";
import { useDashboardOverviewData } from "@/hooks/useDashboardOverviewData";
import { AgencyAnalyticsEmbed } from "@/components/client-analytics/AgencyAnalyticsEmbed";
import { NotConnected, formatLocalDate } from "@/components/client-analytics/primitives";
import {
  LeadsPanel,
  SearchPerformancePanel,
  TrafficPanel,
  LocalPanel,
  AdvertisingPanel,
  LsaPanel,
  PipelinePanel,
  CrmContactsPanel,
  DealTablePanel,
  ContactTablePanel,
  HubSpotAttributionPanel,
  EmailMarketingPanel,
  RevenuePanel,
  InsightsBar,
  SiteHealthPanel,
  ContentPublishedPanel,
  WorkDonePanel,
} from "@/components/client-analytics/dashboardPanels";
import { MetaAdsPanel } from "@/components/client-analytics/frames/MetaAdsPanel";
import { MetaAdsDemographicsPanel } from "@/components/client-analytics/frames/MetaAdsDemographicsPanel";

// LOCAL PRESENCE "not applicable" exception list (Carl ruling, 2026-08-10:
// not-connected -> invite, connected-but-quiet -> show the zero honestly,
// not applicable -> hide silently, no prompt). The first two states are
// keyed off real data (`data.local` null vs. populated); the third has no
// backing field -- there is no `has_physical_location` on
// ClientAnalyticsDashboard, and Local Presence is the only section on this
// page that implicitly assumes every client has a storefront or service
// area a Google Business Profile could represent. Rather than invent a
// schema field for one client, this is a small, explicit, named exception:
// a website only belongs here when the client has been confirmed to have NO
// walk-in/service-area premises, so "connect your Google Business Profile"
// would be a dead-end sales pitch. Add entries (and the reason) here only,
// not scattered elsewhere.
//
// - alibimusic.com (ALIBI Music): B2B production-music licensing sold
//   entirely online -- no walk-in trade, no service area, "local search"
//   is structurally meaningless for this business.
const LOCAL_NOT_APPLICABLE_WEBSITES = new Set<string>(["alibimusic.com"]);

// The page header's period caption, shown on every client dashboard.
// Parsing goes through the shared primitives.parseLocalDate — see its comment
// for why `new Date("YYYY-MM-DD")` rendered a day early in every US timezone.
// The end date carries the year, the start does not ("May 12 – Aug 10, 2026").
// The two bounds are an ATOMIC pair: unless BOTH parse to real calendar dates
// the header shows NO caption (empty string), never a raw or half-valid echo. A
// missing, unparseable, or one-sided-valid bound is no claim — never a literal
// "Invalid Date" and never a bare "not-a-date – Aug 10". This mirrors the rule
// the backend now enforces (_coalesce_period / snapshot_store._validated_period,
// Sol review round 2, 2026-08-11): absence or invalidity produces no claim.
// Exported for the timezone regression test.
export function formatPeriod(start?: string, end?: string): string {
  if (!start || !end) return "";
  const s = formatLocalDate(start, { month: "short", day: "numeric" });
  const e = formatLocalDate(end, { month: "short", day: "numeric", year: "numeric" });
  if (!s || !e) return "";
  return `${s} – ${e}`;
}

export default function ClientDashboardPage({ website }: { website: string }) {
  // No picker (Carl's ruling, 2026-08-11): the broken 7d/28d/90d selector is
  // gone. The backend decides the real window per section (a genuine rolling
  // 30 days where a daily series backs it, the true bake window otherwise) and
  // discloses it in the payload — the client dashboard no longer sends a range
  // to select, and the header caption below reads the REAL window the backend
  // returned rather than echoing one this page chose.
  const { data, isLoading, isError, refetch } = useClientAnalytics(website);
  const embedConfig = useClientAnalyticsEmbed(website);
  const overview = useDashboardOverviewData(website);

  // Configuration is checked before either dashboard surface is shown. React
  // Query keys this request by website and supplies no cross-key placeholder,
  // so the previous tenant's bearer URL disappears as soon as the tenant
  // changes. The response website check is a final defense against a malformed
  // or stale server response.
  // isPending covers the first request and any retry backoff. isLoading is
  // false between retries, which would otherwise flash the native dashboard
  // before we know whether a report exists.
  if (embedConfig.isPending) {
    return (
      <div className="max-w-[1120px] mx-auto px-8 py-16 text-center text-muted-foreground">
        Loading your dashboard…
      </div>
    );
  }

  // A failed config request is not "unconfigured". Falling through to the
  // native dashboard would hide a real report behind a transient 401/5xx.
  if (embedConfig.isError) {
    return (
      <div className="max-w-[1120px] mx-auto px-8 py-16 text-center flex flex-col gap-3">
        <div className="text-dash-title font-semibold">
          We couldn't load your performance report
        </div>
        <div className="text-dash-context text-muted-foreground">
          The report configuration didn't respond. This is usually temporary.
        </div>
        <button
          onClick={() => {
            void embedConfig.refetch();
          }}
          className="self-center rounded-md bg-coral hover:bg-coral-hover text-white px-4 py-2 text-dash-subtitle font-semibold"
        >
          Try again
        </button>
      </div>
    );
  }

  const embedUrl =
    embedConfig.data?.website === website && embedConfig.data.configured
      ? embedConfig.data.embed_url
      : null;

  if (embedUrl) {
    return (
      <div className="min-h-screen bg-background text-foreground">
        <div className="mx-auto flex max-w-[1440px] flex-col gap-6 px-4 pb-16 pt-8 sm:px-6 lg:px-8">
          <header className="flex flex-col gap-1">
            <h1 className="text-dash-page font-bold">{website}</h1>
            <div className="text-dash-context text-muted-foreground">
              Performance dashboard · prepared by Agile &amp; Co.
            </div>
          </header>

          <AgencyAnalyticsEmbed website={website} embedUrl={embedUrl} />

          <div className="mx-auto mt-2 flex w-full max-w-[1120px] flex-col gap-6 border-t border-border pt-8">
            <div>
              <h2 className="text-dash-title font-semibold">Your work in Core</h2>
              <p className="mt-1 text-dash-context text-muted-foreground">
                Site health and recent publishing activity from your Core workspace.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              <div className="min-w-0">
                <SiteHealthPanel data={overview.siteHealth} linkTo="/overview/site-health" />
              </div>
              <div className="min-w-0">
                <ContentPublishedPanel data={overview.content} linkTo="/content/planner" />
              </div>
            </div>

            <WorkDonePanel items={overview.workDone} />

            <footer className="flex flex-col gap-2 pt-2 text-dash-context text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
              <div>Agile &amp; Co. · Core</div>
              <div>Questions about these numbers? Message your account manager.</div>
            </footer>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="max-w-[1120px] mx-auto px-8 py-16 text-center text-muted-foreground">
        Loading your dashboard…
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="max-w-[1120px] mx-auto px-8 py-16 text-center flex flex-col gap-3">
        <div className="text-dash-title font-semibold">We couldn't load your dashboard</div>
        <div className="text-dash-context text-muted-foreground">
          The data service didn't respond. This is usually temporary.
        </div>
        <button
          onClick={() => refetch()}
          className="self-center rounded-md bg-coral hover:bg-coral-hover text-white px-4 py-2 text-dash-subtitle font-semibold"
        >
          Try again
        </button>
      </div>
    );
  }

  if (!data.connected) {
    return (
      <div className="max-w-[1120px] mx-auto px-8 py-10">
        <h1 className="text-dash-page font-bold">{website}</h1>
        <div className="mt-6">
          <NotConnected message="Your analytics aren't connected yet" cta="Talk to your account manager" />
        </div>
      </div>
    );
  }

  // The backend's REAL primary window, coerced null->undefined so the pinned
  // formatPeriod contract (string | undefined) is unchanged; "" when no range.
  const periodLabel = formatPeriod(data.period_start ?? undefined, data.period_end ?? undefined);

  return (
    <div className="bg-background text-foreground min-h-screen">
      <div className="max-w-[1120px] mx-auto px-8 pt-8 pb-16 flex flex-col gap-6">
        {/* HEADER */}
        <div className="flex items-center justify-between">
          <div className="flex flex-col gap-1">
            <h1 className="text-dash-page font-bold">{data.website}</h1>
            <div className="text-dash-context text-muted-foreground">
              Performance dashboard · prepared by Agile &amp; Co.
            </div>
          </div>
          <div className="flex flex-col items-end gap-1.5">
            {/* No picker. This reads the backend's REAL primary window (rolling
                30 days when a daily-capable section derived one, else the true
                bake window); nothing renders when the backend claims no range at
                all (never a fabricated one — see formatPeriod). */}
            {periodLabel && (
              <div className="text-dash-context tabular text-muted-foreground">
                {periodLabel}
              </div>
            )}
          </div>
        </div>

        {/* 0. INSIGHTS — slim "what changed" headline strip, biggest movers first */}
        {data.insights?.movers?.length ? <InsightsBar movers={data.insights.movers} /> : null}

        {/* 1. LEADS — hero number, client value first. Always mounted
            unconditionally (same adaptive pattern as Advertising/
            MetaAdsDemographics below): LeadsPanel self-gates to its own
            NotConnected invite when data.leads is null (WhatConverts never
            wired up) vs. rendering the real, possibly-zero, KPI strip when
            it is (see LeadsPanel's doc comment). There is no "not
            applicable" state for Leads -- every client either has lead
            tracking or doesn't. */}
        <LeadsPanel data={data.leads} periodEnd={data.period_end} />

        {/* 2. SEARCH PERFORMANCE — dominant band: GSC KPIs + trend + keywords */}
        <SearchPerformancePanel
          search={overview.search}
          rankings={data.rankings ?? null}
          linkTo="/overview/top-pages"
          rankDistribution={data.rank_distribution?.over_time}
          multiEngineRanks={data.multi_engine_ranks?.rows}
          multiEngineRanksTotal={data.multi_engine_ranks?.total_rows}
          periodEnd={data.period_end}
        />

        {/* 3. TRAFFIC — single GA4 band */}
        {data.traffic && (
          <TrafficPanel data={data.traffic} linkTo="/overview/traffic" periodEnd={data.period_end} />
        )}

        {/* 4. LOCAL PRESENCE + reviews. Three states per Carl's ruling: real
            data renders LocalPanel as before (including a connected-but-
            quiet zero -- LocalPanel already shows that honestly, see its own
            comments; deliberately NOT adding new "quiet" copy here -- a
            separate fix is landing for a map_local bug that under-reads
            locations[] as zero when real traffic exists one level down, so
            any quiet-state messaging added here right now risks telling a
            client with real traffic that they have none). No data at all
            shows the invite UNLESS the client is in
            LOCAL_NOT_APPLICABLE_WEBSITES (see that const's comment) -- those
            clients hide silently instead. */}
        {data.local ? (
          <LocalPanel local={data.local} reviews={data.reviews} periodEnd={data.period_end} />
        ) : LOCAL_NOT_APPLICABLE_WEBSITES.has(data.website) ? null : (
          <NotConnected
            message="Local Presence — Google Business Profile isn't connected for this account"
            cta="Talk to your account manager"
          />
        )}

        {/* 5. PAID MEDIA — Google Ads then Meta Ads, each a full-width band
            of its own, both always mounted and never conditional on the
            platform being connected. Ruling (Carl, 2026-08-10): an absent
            section reads as "this product has no Meta feature"; an empty
            state reads as "you're not running Meta yet" -- the second is
            honest and the first isn't, so neither panel is gated on its data
            being present. Each self-gates to its own adaptive NotConnected
            internally when its data is null/undefined (Tyler McBroom /
            trm.cpa is Meta-only, Alamo City is Google-only, most clients
            today are Google-only or neither -- all four combinations render
            through this one unconditional layout with no special-casing).

            Full-width, NOT the two-up row these two used to share (Carl,
            2026-08-10, superseding the earlier two-up call): Meta Ads is a
            whole AA report page in one panel -- clicks trend, clicks by
            campaign, an 8-tile KPI row, spend trend, clicks by ad set,
            clicks by ad, and the campaign/ad-set/ad breakdown table. At half
            width its two-chart top row collapsed to `lg:flex-row`'s stacked
            fallback, the KPI row wrapped to four ragged lines, and the
            breakdown table scrolled sideways inside ~470px. Advertising is
            the same shape, smaller. Giving each its own band is what makes
            those internal layouts resolve as designed -- and it's why the
            columns no longer need min-w-0 here: there is no sibling left to
            push off the edge. Site Health / Content Published below stay
            two-up on purpose; those are small and genuinely pair. */}
        <AdvertisingPanel data={data.ads} periodEnd={data.period_end} />

        {/* 5a. LOCAL SERVICES ADS — a distinct AA raw-asset section, never
            folded into GA4 conversions or the general Google Ads panel.
            An absent section means the LSA connector was never connected and
            stays hidden. A present section always renders, including a true
            connected-but-zero period, so quiet accounts are not mislabeled as
            disconnected. */}
        {data.lsa && <LsaPanel data={data.lsa} />}

        <MetaAdsPanel data={data.meta_ads} periodEnd={data.period_end} />

        {/* 5b. META ADS DEMOGRAPHICS — AA "Meta Ads | Demographics" report
            page parity (gender/age donuts, KPI tiles, per-country table).
            Full-width, its own band below Paid Media, not squeezed into the
            two-up row above — see MetaAdsDemographicsPanel's module
            docstring for the map-vs-table layout call.
            Always mounted (fix 2026-08-10, same ruling as the Paid Media
            row's f988cba60): a `data.meta_ads &&` gate here made this
            section vanish entirely rather than show its own NotConnected
            for the 27 of 40 clients without Meta -- absent reads as "this
            product has no Demographics feature", empty reads as "you're
            not running Meta yet", and only the second is honest. The panel
            already self-gates on `metaAds` being null/undefined AND on
            `metaAds.demographics` being unpopulated internally, so no
            special-casing is needed here. */}
        <MetaAdsDemographicsPanel metaAds={data.meta_ads} />

        {/* 6. PIPELINE — CRM deal funnel, only when a CRM provider is connected */}
        {data.crm && <PipelinePanel data={data.crm} />}

        {/* 6b. CRM CONTACTS — HubSpot contacts KPI row + lifecycle breakdown,
            only when the CRM connector's contacts pull returned data */}
        {data.crm?.contacts && <CrmContactsPanel data={data.crm.contacts} />}

        {/* 6d. DEAL TABLE — deal-level detail rows below the funnel/contacts,
            only when the CRM pull actually has per-deal rows (DealTablePanel
            self-gates on recent_deals.length, e.g. zero for Precision
            Restorations) */}
        {data.crm && <DealTablePanel data={data.crm} />}

        {/* 6e. CONTACT TABLE — raw per-contact detail rows below the
            lifecycle/country aggregates, only when the CRM contacts pull
            actually has per-contact rows (ContactTablePanel self-gates on
            recent_contacts.length) */}
        {data.crm?.contacts && <ContactTablePanel data={data.crm.contacts} />}

        {/* 6c. HUBSPOT PAID ATTRIBUTION — paid-search/paid-social sessions +
            contacts, only for the 3 clients with real HubSpot paid-channel
            traffic (ALIBI Music, Tyler McBroom, Precision Restorations) */}
        {data.hubspot_attribution && (
          <HubSpotAttributionPanel data={data.hubspot_attribution} periodEnd={data.period_end} />
        )}

        {/* 6f. EMAIL MARKETING — a top-level SIBLING of the CRM family
            above (Pipeline/Contacts/Deal Table/Contact Table/Paid
            Attribution), deliberately NOT nested inside any `data.crm &&`
            gate. Krug's design-review verdict (2026-08-10): email is a
            marketing surface, not a sales/CRM relationship, even though
            HubSpot is currently the only connected provider for both —
            see EmailMarketingPanel's own doc comment for the full ruling.
            Always mounted unconditionally (same adaptive pattern as
            Advertising/Meta Ads/HubSpot Paid Attribution below/above):
            EmailMarketingPanel self-gates to its own NotConnected invite
            when data.email_marketing is null (no email platform ever
            pulled) vs. the real, possibly-zero, KPI strip when it's
            connected but quiet (Precision Restorations). */}
        <EmailMarketingPanel data={data.email_marketing} />

        {/* 7. REVENUE — ecommerce KPIs + trend. Hides silently ("not
            applicable", Carl ruling 2026-08-10) whenever revenue AND
            transactions are both zero -- a lead-gen business (plumber,
            contractor, CPA) doesn't sell online, so a connected-but-zero
            ecommerce pull is not a prompt to connect a store, it's just
            irrelevant to how that business operates. This is deliberately a
            bare truthy-object check on `data.ecommerce`, NOT distinguishing
            "never connected" from "connected but genuinely zero" -- both
            read identically to the client (nothing rendered), which is
            exactly the point: never surface "$0 revenue, 0 transactions" to
            a business that was never going to have any. See TASK 1,
            2026-08-10 -- commit d50096d0f fixed the upstream builder so a
            connected-but-zero client now returns a real
            {revenue: 0, transactions: 0} section instead of an absent one
            (32 of 40 clients flip from absent to present-zero once the
            tracked snapshot is rebuilt); without this guard, all 32 would
            get a dead-end "$0 revenue" panel. */}
        {data.ecommerce && (data.ecommerce.revenue !== 0 || data.ecommerce.transactions !== 0) && (
          <RevenuePanel data={data.ecommerce} />
        )}

        {/* 8. SITE HEALTH + CONTENT PUBLISHED — two-up.
            min-w-0 for the same reason as the Paid Media row above. */}
        <div className="flex gap-6 items-stretch">
          <div className="flex-1 min-w-0">
            <SiteHealthPanel data={overview.siteHealth} linkTo="/overview/site-health" />
          </div>
          <div className="flex-1 min-w-0">
            <ContentPublishedPanel data={overview.content} linkTo="/content/planner" />
          </div>
        </div>

        {/* 9. WHAT WE DID — terminal accomplishment feed */}
        <WorkDonePanel items={overview.workDone} />

        {/* FOOTER */}
        <div className="flex items-center justify-between pt-2">
          <div className="text-dash-context text-muted-foreground">Agile &amp; Co. · Core</div>
          <div className="text-dash-context text-muted-foreground">
            Questions about these numbers? Message your account manager.
          </div>
        </div>
      </div>
    </div>
  );
}
